import { useState } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useFirms } from '@/hooks/useFirms';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFirmContextStore } from '@/stores/useFirmContextStore';

export function FirmContextSelector() {
  const { user } = useAuthStore();

  if (!user?.isSuperAdmin) return null;

  return <FirmContextSelectorInner />;
}

function FirmContextSelectorInner() {
  const [open, setOpen] = useState(false);
  const { data: firms = [], isLoading } = useFirms({ enabled: true });
  const { activeFirmId, setActiveFirmId } = useFirmContextStore();

  const selectedFirm = firms.find((firm) => firm.firm_id === activeFirmId);
  const label = selectedFirm
    ? `${selectedFirm.name} (${selectedFirm.firm_id})`
    : isLoading
      ? 'Loading firms...'
      : 'All firms';

  const handleSelect = (firmId: number | null) => {
    setActiveFirmId(firmId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="firm-context-selector"
          className="mr-3 w-[260px] justify-between"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{label}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0">
        <Command>
          <CommandInput placeholder="Search firms..." />
          <CommandList>
            <CommandEmpty>No firm found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="All firms" onSelect={() => handleSelect(null)}>
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    activeFirmId === null ? 'opacity-100' : 'opacity-0'
                  )}
                />
                All firms
              </CommandItem>
              {firms.map((firm) => (
                <CommandItem
                  key={firm.firm_id}
                  value={`${firm.name} (${firm.firm_id})`}
                  onSelect={() => handleSelect(firm.firm_id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      activeFirmId === firm.firm_id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {firm.name} ({firm.firm_id})
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
